/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */
import {ActionTree, Commit} from 'vuex';
import {JsonLdCollection, RootState} from '@/store/types';
import {AxiosResponse} from 'axios';
import {api} from '@/services/http';
import {
    ChallengeAchievementInterface, CommunityChallengeAchievementsRequestInterface,
    SpeciesAchievementInterface,
    TrophyCabinetMode,
    TrophyStateInterface,
} from '@/store/modules/trophy-cabinet/types';

/**
 * Actions for trophy cabinet operations.
 *
 * Actions are functions that can be exported and called upon potentially in an asynchronous manner.
 * To add an action export a named const function. e.g. export const fnName = ({ commit }, {id, name}) => { ... }
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const actions: ActionTree<TrophyStateInterface, RootState> = {
    /**
     * Load species achievements.
     * @param {Commit} commit
     * @param {TrophyStateInterface} state
     * @param {object} params
     * @return {Promise<JsonLdCollection<SpeciesAchievementInterface>>}
     */
    async getSpeciesAchievements(
        {commit, state}: {commit: Commit, state: TrophyStateInterface},
        params: object = {},
    ): Promise<JsonLdCollection<SpeciesAchievementInterface>> {
        try {
            commit('start');
            const response: AxiosResponse<JsonLdCollection<SpeciesAchievementInterface>> = await api.get('api/zoo/species_stats');

            commit('setSpeciesAchievements', response.data['hydra:member']);
            commit('stop', true);
            return response.data;
        } catch (e) {
            commit('stop', false);
            throw e;
        }
    },

    /**
     * Load community challenge achievements.
     * @param {Commit} commit
     * @param {TrophyStateInterface} state
     * @param {object} params
     * @return {Promise<ChallengeAchievementInterface>}
     */
    async getChallengeAchievements(
        {commit, state}: {commit: Commit, state: TrophyStateInterface},
        params: CommunityChallengeAchievementsRequestInterface,
    ): Promise<ChallengeAchievementInterface> {
        const response: AxiosResponse<ChallengeAchievementInterface> =
            await api.post('api/zoo/challenges/trophies', params);
        commit('setCommunityChallengeAchievements', response.data);
        return response.data;
    },

    /**
     * Update the selection mode of state
     * @param {Commit} commit
     * @param {TrophyStateInterface} state
     * @param {TrophyCabinetMode} mode
     */
    setMode({commit, state}: {commit: Commit, state: TrophyStateInterface}, mode: TrophyCabinetMode): void {
        if (mode === state.mode) {
            return;
        }

        commit('setMode', mode);
    },

    /**
     * Reset all trophy lists.
     * @param {Commit} commit
     */
    reset({commit}: {commit: Commit}): void {
        commit('reset');
    },

};
